/**
 * Pure tree math for parent/child row drag (I-390, recursive since I-392).
 *
 * `data` is a flat array in pre-order: every node is directly followed by
 * its subtree, nested arbitrarily deep. Top-level rows are children of a
 * virtual root (`null`). These helpers only compute indices — they never
 * touch the DOM or Vue reactivity, so they are unit-testable.
 */

export type RowBlock = { start: number; size: number };

export type TreeInfo = {
  /** rowIdx -> subtree size including the row itself */
  sizeOf: number[];
  /** rowIdx -> row index of the direct parent, null for top-level rows */
  parentOfRow: (number | null)[];
  /** parent row idx (null = virtual root) -> direct children row indices, in order */
  childrenOf: Map<number | null, number[]>;
};

export function deriveTree<T>(
  data: readonly T[],
  rowIdKey: (row: T) => string,
  rowParentId: (row: T) => string | null,
  warn?: (message: string) => void
): TreeInfo {
  const sizeOf: number[] = [];
  const parentOfRow: (number | null)[] = [];
  const childrenOf = new Map<number | null, number[]>([[null, []]]);
  // Ancestor chain of the previous row (including the row itself). A row's
  // parent must sit on this stack, otherwise its subtree would not be
  // contiguous in the flat array.
  const stack: number[] = [];

  for (let i = 0; i < data.length; i++) {
    const parentId = rowParentId(data[i]);
    if (parentId === null) {
      stack.length = 0;
    } else {
      while (stack.length > 0 && rowIdKey(data[stack[stack.length - 1]]) !== parentId) {
        stack.pop();
      }
      if (stack.length === 0) {
        // Invariant violation: the referenced parent is not in the ancestor
        // chain (unknown id, forward reference, or a non-contiguous child).
        // Degrade to a top-level row.
        warn?.(
          `NxTable rowParentId: row "${rowIdKey(data[i])}" references parent "${parentId}" ` +
            `which is not among its preceding ancestors; treating it as a top-level row.`
        );
      }
    }
    const parent = stack.length > 0 ? stack[stack.length - 1] : null;
    parentOfRow.push(parent);
    sizeOf.push(1);
    const siblings = childrenOf.get(parent);
    if (siblings) {
      siblings.push(i);
    } else {
      childrenOf.set(parent, [i]);
    }
    stack.push(i);
  }

  // Subtree sizes: children always have higher indices than their parent,
  // so a single backwards pass accumulates every subtree.
  for (let i = data.length - 1; i >= 0; i--) {
    const parent = parentOfRow[i];
    if (parent !== null) {
      sizeOf[parent] += sizeOf[i];
    }
  }

  return { sizeOf, parentOfRow, childrenOf };
}

/** unit = the rows that travel together when dragging the row at dragIdx: its whole subtree */
export function dragUnitOf(info: TreeInfo, dragIdx: number): RowBlock {
  return { start: dragIdx, size: info.sizeOf[dragIdx] };
}

/**
 * Sorted valid final target indices (`onRowMoved` "to" semantics: index of
 * the unit's first row after removal + reinsert). A node reorders only among
 * its direct siblings — valid positions are the sibling-subtree boundaries
 * inside the parent's span, never inside a sibling's subtree (that would be
 * a re-parent). Includes the no-op target (= dragIdx).
 */
export function validTargets(info: TreeInfo, dragIdx: number, dataLen: number): number[] {
  const parent = info.parentOfRow[dragIdx];
  const siblings = info.childrenOf.get(parent) ?? [];
  const size = info.sizeOf[dragIdx];
  const spanEnd = parent === null ? dataLen : parent + info.sizeOf[parent];
  const targets = new Set<number>([dragIdx, spanEnd - size]);
  for (const sibling of siblings) {
    if (sibling < dragIdx) targets.add(sibling);
    if (sibling > dragIdx) targets.add(sibling - size);
  }
  return [...targets].sort((a, b) => a - b);
}

/**
 * Nearest valid target for a raw drop-zone target. Distance ties resolve
 * toward the shorter move (closer to `from`), then the smaller index.
 */
export function clampTarget(targets: number[], raw: number, from: number): number {
  let best = targets[0];
  for (const t of targets) {
    const d = Math.abs(t - raw) - Math.abs(best - raw);
    if (d < 0 || (d === 0 && Math.abs(t - from) < Math.abs(best - from))) {
      best = t;
    }
  }
  return best;
}
